import { ref } from 'vue'
import { add } from 'date-fns'
import { APPOINTMENT_BEFORE } from '@/stores/Constant'

interface NoAppointment {
  isAllDay: boolean
  datetime: number
  startTime: string
  endTime: string
}

interface DoctorCalendar {
  start: string
  end: string
}

interface TimeSlot {
  start: string
  end: string
  active: boolean
  density_color: string | null
}

interface AppointmentSettings {
  appoint_liff_start_day: number
  appoint_liff_end_day: number
  appoint_liff_hiding_doctor: boolean
  appoint_liff_select_doctor_without_workhours: boolean
}

// ข้อมูลสีแสดงความหนาแน่นของนัดหมาย
export interface DensityColorLabel {
  color: string
  description: string
  description_en?: string
  description_cn?: string
}

export function useAppointmentCalendar() {
  // Calendar state
  const showCalendar = ref(false)
  const dateSelect = ref<Date | null>(null)
  const dateText = ref('')
  const calendarMinDate = ref<Date | null>(null)
  const calendarMaxDate = ref<Date | null>(null)

  // Time slot state
  const clinicWorkHour = ref<TimeSlot[]>([])
  const workHourDensityColorLabel = ref<DensityColorLabel[]>([])

  const selectedTime = ref<any[]>([])
  const selectedTimeIndex = ref<number[]>([])
  const loadingWorkHour = ref(false)

  /**
   * Format date to dd/MM format
   */
  function formatDate(date: Date): string {
    return `${date.getDate()}/${date.getMonth() + 1}`
  }

  /**
   * Convert HH:MM time string to seconds
   */
  function convertSecond(hm: string): number {
    const t = hm.split(':')
    return +t[0] * 60 * 60 + +t[1] * 60
  }

  /**
   * Set calendar min/max dates based on settings
   */
  function setCalendarDate(settings: AppointmentSettings) {
    calendarMinDate.value = add(new Date(Date.now()), {
      days: settings.appoint_liff_start_day,
    })
    calendarMaxDate.value = add(new Date(Date.now()), {
      days: settings.appoint_liff_end_day,
    })
  }

  function setWorkHourDensityColorLabel(densityDescription?: DensityColorLabel[] | null) {
    workHourDensityColorLabel.value = densityDescription ?? []
  }

  /**
   * Map and filter time slots based on availability
   */
  function mappingWorkingHourSlot(
    working_hour_slot: TimeSlot[] | null | undefined,
    date: Date,
    noAppointments: NoAppointment[],
    settings: AppointmentSettings,
    doctorCalendars: DoctorCalendar[],
    doctorId: string | null,
    formDate: string,
  ): TimeSlot[] {
    const now = new Date()
    const select_epochdate = new Date(date).setHours(0, 0, 0, 0)
    const slots = Array.isArray(working_hour_slot) ? working_hour_slot : []

    // Filter by no-appointment dates (partial day blocks)
    let working_hour_slot_result = slots.map((t: TimeSlot) => {
      let active = t.active
      const noAppoints = noAppointments.filter((x: NoAppointment) => x.datetime === select_epochdate && !x.isAllDay)
      if (noAppoints.length > 0) {
        const start1 = convertSecond(t.start) + 1
        const end1 = convertSecond(t.end)
        for (const noAppoint of noAppoints) {
          const start2 = convertSecond(noAppoint.startTime)
          const end2 = convertSecond(noAppoint.endTime)
          if ((start1 < end2 && start1 > start2) || (start2 < end1 && start2 > start1)) {
            active = false
            break
          }
        }
      }
      const slotDate = new Date(date)
      const [startHour, startMinute] = t.start.split(':').map(Number)
      slotDate.setHours(startHour, startMinute, 0, 0)
      if (slotDate < add(now, APPOINTMENT_BEFORE)) {
        active = false
      }
      return { ...t, active }
    })

    // Filter by doctor schedule if applicable
    if (
      !settings.appoint_liff_hiding_doctor &&
      !settings.appoint_liff_select_doctor_without_workhours &&
      doctorId !== null &&
      doctorId !== '0'
    ) {
      const doctorSchedule = doctorCalendars.find((element: DoctorCalendar) => element.start.split('T')[0] === formDate)
      if (doctorSchedule) {
        working_hour_slot_result = getTime(working_hour_slot_result, doctorSchedule)
      }
    }

    // เปลี่ยนให้ density color เป็น null ถ้า time slot นั้น ไม่ active แล้ว
    // (เนื่องจากไม่สามารถเลือก time slot ที่ไม่ active ได้ จึงไม่จำเป็นต้องแสดงสี)
    working_hour_slot_result = working_hour_slot_result.map((slot: TimeSlot) => {
      if (!slot.active) slot.density_color = null
      return slot
    })

    return working_hour_slot_result
  }

  /**
   * Filter time slots by doctor's work schedule
   */
  function getTime(clinic_work_hour: TimeSlot[], doctor_time: DoctorCalendar): TimeSlot[] {
    const doctor_start = new Date(doctor_time.start).toLocaleTimeString([], { hour12: false })
    const doctor_end = new Date(doctor_time.end).toLocaleTimeString([], { hour12: false })
    return clinic_work_hour.map((t: TimeSlot) => {
      const start1 = convertSecond(t.start) + 1
      const end1 = convertSecond(t.end)
      const start2 = convertSecond(doctor_start)
      const end2 = convertSecond(doctor_end)
      if ((start1 < end2 && start1 > start2) || (start2 < end1 && start2 > start1)) {
        return { ...t }
      }
      return { ...t, active: false }
    })
  }

  /**
   * Handle time slot selection
   */
  function onTimeSelect(i: number) {
    // Single select time
    selectedTimeIndex.value = [i]
    selectedTime.value = [{ index: i, data: clinicWorkHour.value[i] }]
  }

  /**
   * Calendar day formatter for disabled dates and doctor availability
   */
  function formatter(
    day: any,
    noAppointments: NoAppointment[],
    settings: AppointmentSettings,
    doctorCalendarsIn: number[],
    doctorId: string | null,
    today: Date,
    t: (key: string) => string,
  ) {
    const calendarDate = day.date.setHours(0, 0, 0, 0)
    if (today.toDateString() === day.date.toDateString()) {
      day.bottomInfo = t('appointmentrequest.today')
    }
    const noAppointmentDates = noAppointments.filter((x: NoAppointment) => x.datetime === calendarDate)
    const isAllDay = noAppointmentDates.some((x: NoAppointment) => x.isAllDay)
    if (isAllDay) {
      day.topInfo = t('appointmentrequest.no_appoint')
      day.type = 'disabled'
    }
    if (settings.appoint_liff_hiding_doctor || doctorId === null || doctorId === '0') return day
    if (settings.appoint_liff_select_doctor_without_workhours) return day
    if (doctorCalendarsIn.includes(calendarDate)) {
      day.topInfo = t('appointmentrequest.doctor_in')
    } else {
      day.type = 'disabled'
    }
    return day
  }

  /**
   * Handle calendar date confirmation
   */
  async function onConfirmCalendar(date: Date, fetchTimeSlotsCallback: (date: Date) => Promise<void>) {
    showCalendar.value = false
    dateText.value = formatDate(date)
    dateSelect.value = date
    loadingWorkHour.value = true
    await fetchTimeSlotsCallback(date)
    loadingWorkHour.value = false
  }

  /**
   * Clear time selection
   */
  function clearTimeSelection() {
    selectedTime.value = []
    selectedTimeIndex.value = []
  }

  /**
   * Clear date selection
   */
  function clearDateSelection() {
    dateText.value = ''
    dateSelect.value = null
    showCalendar.value = false
    clinicWorkHour.value = []
    workHourDensityColorLabel.value = []
    loadingWorkHour.value = false
    clearTimeSelection()
  }

  return {
    // State
    showCalendar,
    dateSelect,
    dateText,
    calendarMinDate,
    calendarMaxDate,
    clinicWorkHour,
    workHourDensityColorLabel,
    selectedTime,
    selectedTimeIndex,
    loadingWorkHour,
    // Utilities
    formatDate,
    convertSecond,
    setCalendarDate,
    setWorkHourDensityColorLabel,
    // Time slot logic
    mappingWorkingHourSlot,
    getTime,
    onTimeSelect,
    // Calendar
    formatter,
    onConfirmCalendar,
    // Clear functions
    clearTimeSelection,
    clearDateSelection,
  }
}
